import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '../context/ThemeContext';
import dataStore from '../data/dataStore';

const RoomCardPrint = ({ isOpen, onClose, currentMonday }) => {
  const { darkMode } = useTheme();
  const printRef = useRef(null);
  
  const [rooms, setRooms] = useState([]);
  const [users, setUsers] = useState([]);
  const [children, setChildren] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [childSchedule, setChildSchedule] = useState({});
  const [weekTheme, setWeekTheme] = useState('');
  const [roomNotes, setRoomNotes] = useState({});
  const [announcements, setAnnouncements] = useState([]);
  
  // Utility rooms (Breaks, Lunch) - shown as sections on each page
  const [utilityRooms, setUtilityRooms] = useState([]);
  const [utilitySchedules, setUtilitySchedules] = useState({}); // { roomId: { dateStr: [assignments] } }
  
  // Room selection for printing
  const [selectedRoomIds, setSelectedRoomIds] = useState([]);
  
  // Print options
  const [includeStudents, setIncludeStudents] = useState(true);
  const [printMode, setPrintMode] = useState('rooms'); // 'rooms' or 'teacherSummary'
  
  // Load all data when modal opens
  useEffect(() => {
    if (isOpen && currentMonday) {
      const allRooms = dataStore.getRooms();
      const allUsers = dataStore.getUsers().filter(u => u.role === 'USER' || u.role === 'ADMIN');
      const allChildren = dataStore.getChildren().filter(c => c.status === 'active');
      
      // Separate utility rooms from regular rooms using the isUtility flag
      const utils = allRooms.filter(r => r.isUtility === true);
      const regular = allRooms.filter(r => r.isUtility !== true);
      
      setUtilityRooms(utils);
      setRooms(regular);
      setUsers(allUsers);
      setChildren(allChildren);
      
      // Select all regular rooms by default
      setSelectedRoomIds(regular.map(r => r.id));
      
      // Load schedule data
      const mondayStr = formatDate(currentMonday);
      const weekSchedule = dataStore.getWeekSchedule(mondayStr);
      setSchedule(weekSchedule);
      
      // Load child schedule for the week
      const weekDates = getWeekDates(currentMonday);
      const childSched = {};
      weekDates.forEach(date => {
        const dateStr = formatDate(date);
        childSched[dateStr] = dataStore.getChildScheduleForDate(dateStr);
      });
      setChildSchedule(childSched);
      
      // Extract utility room schedules
      const utilScheds = {};
      utils.forEach(utilRoom => {
        utilScheds[utilRoom.id] = {};
        weekDates.forEach(date => {
          const dateStr = formatDate(date);
          const daySchedule = weekSchedule[dateStr] || {};
          utilScheds[utilRoom.id][dateStr] = daySchedule[utilRoom.id] || [];
        });
      });
      setUtilitySchedules(utilScheds);
      
      // Load week theme
      const themeNote = dataStore.getDailyNote(mondayStr);
      if (themeNote && themeNote.type === 'week_theme') {
        setWeekTheme(themeNote.note);
      } else {
        setWeekTheme('');
      }
      
      // Load room notes for the week
      const notesMap = {};
      weekDates.forEach(date => {
        const dateStr = formatDate(date);
        regular.forEach(room => {
          const notes = dataStore.getRoomDayNotes(room.id, dateStr);
          if (notes && notes.length > 0) {
            const key = `${dateStr}_${room.id}`;
            notesMap[key] = notes;
          }
        });
      });
      setRoomNotes(notesMap);
      
      // Load announcements for the week
      setAnnouncements(dataStore.getAnnouncementsForWeek(mondayStr));
    }
  }, [isOpen, currentMonday]);
  
  // Helper functions
  const formatDate = (date) => {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  };
  
  const getWeekDates = (monday) => {
    const dates = [];
    for (let i = 0; i < 5; i++) {
      const d = new Date(monday);
      d.setDate(d.getDate() + i);
      dates.push(d);
    }
    return dates;
  };
  
  const formatTime = (time) => {
    if (!time) return '—';
    const [hours, minutes] = time.split(':');
    const h = parseInt(hours);
    const suffix = h >= 12 ? 'p' : 'a';
    const hour12 = h > 12 ? h - 12 : (h === 0 ? 12 : h);
    return minutes === '00' ? `${hour12}${suffix}` : `${hour12}:${minutes}${suffix}`;
  };
  
  const formatTimeRange = (start, end) => {
    if (!start || !end) return '—';
    return `${formatTime(start)}-${formatTime(end)}`;
  };
  
  const getWeekDateRange = () => {
    const weekDates = getWeekDates(currentMonday);
    const startDate = weekDates[0].toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    const endDate = weekDates[4].toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
    return `${startDate} - ${endDate}`;
  };
  
  const getDayAbbrev = (index) => {
    return ['Mon', 'Tue', 'Wed', 'Thu', 'Fri'][index];
  };
  
  // Format date of birth for display (MM/DD/YY)
  const formatBirthday = (dateOfBirth) => {
    if (!dateOfBirth) return '';
    const [year, month, day] = dateOfBirth.split('-');
    return `${month}/${day}/${year.slice(2)}`;
  };
  
  // Room selection handlers
  const toggleRoom = (roomId) => {
    setSelectedRoomIds(prev => 
      prev.includes(roomId) 
        ? prev.filter(id => id !== roomId)
        : [...prev, roomId]
    );
  };
  
  const selectAllRooms = () => {
    setSelectedRoomIds(rooms.map(r => r.id));
  };
  
  const deselectAllRooms = () => {
    setSelectedRoomIds([]);
  };
  
  // Get staff assignments for a room on a specific date
  const getStaffForRoomDate = (roomId, dateStr) => {
    const daySchedule = schedule[dateStr] || {};
    const roomAssignments = daySchedule[roomId] || [];
    return roomAssignments.map(assignment => {
      const user = users.find(u => u.id === assignment.userId);
      return {
        ...assignment,
        user,
        name: user ? user.name : 'Unknown'
      };
    });
  };
  
  // Get children assigned to a room on a specific date
  const getChildrenForRoomDate = (roomId, dateStr) => {
    const dayChildSchedule = childSchedule[dateStr] || {};
    const results = [];
    
    Object.entries(dayChildSchedule).forEach(([childId, assignment]) => {
      if (assignment.roomId === roomId) {
        const child = children.find(c => c.id === childId);
        if (child) {
          results.push({
            ...assignment,
            child,
            name: `${child.firstName} ${child.lastName}`,
            isVisiting: assignment.isVisit || false
          });
        }
      }
    });
    
    return results;
  };
  
  // Get room notes for a room across the week (excluding theme - it's in header)
  const getRoomWeekNotes = (roomId) => {
    const weekDates = getWeekDates(currentMonday);
    const notes = [];
    weekDates.forEach((date, index) => {
      const dateStr = formatDate(date);
      const key = `${dateStr}_${roomId}`;
      const dayNotes = roomNotes[key] || [];
      dayNotes.forEach(note => {
        notes.push({
          day: getDayAbbrev(index),
          text: note.text
        });
      });
    });
    return notes;
  };
  
  // Handle print
  const handlePrint = () => {
    const printContent = printRef.current;
    if (!printContent) return;
    
    const printWindow = window.open('', '_blank');
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Room Schedules - ${getWeekDateRange()}</title>
        <style>
          * { margin: 0; padding: 0; box-sizing: border-box; }
          @page { size: letter portrait; margin: 0.5in; }
          body { font-family: Arial, sans-serif; font-size: 11px; color: #000; background: #fff; }
          .room-card {
            page-break-after: always;
            padding: 20px;
            border: 2px solid #333;
            border-radius: 8px;
            min-height: calc(100vh - 1in);
          }
          .room-card:last-child { page-break-after: auto; }
          .card-header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            border-bottom: 2px solid #333;
            padding-bottom: 10px;
            margin-bottom: 15px;
          }
          .room-name { font-size: 24px; font-weight: bold; margin: 0; }
          .room-ratio { font-size: 14px; color: #555; margin-top: 4px; }
          .header-right { text-align: right; }
          .week-range { font-size: 14px; font-weight: bold; }
          .week-theme { font-size: 12px; color: #555; margin-top: 4px; }
          .section { 
            margin-bottom: 15px; 
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .section-title {
            font-size: 12px;
            font-weight: bold;
            text-transform: uppercase;
            letter-spacing: 1px;
            margin-bottom: 8px;
            color: #333;
            border-bottom: 1px solid #ccc;
            padding-bottom: 4px;
          }
          .schedule-table { 
            width: 100%; 
            border-collapse: collapse; 
            font-size: 10px; 
            break-inside: avoid;
            page-break-inside: avoid;
            table-layout: fixed;
          }
          .schedule-table th, .schedule-table td {
            border: 1px solid #ccc;
            padding: 6px 8px;
            text-align: center;
            overflow: hidden;
            text-overflow: ellipsis;
          }
          .schedule-table th { background: #f0f0f0; font-weight: bold; }
          .schedule-table td:first-child, .schedule-table th:first-child { text-align: left; font-weight: 500; width: 22%; }
          .schedule-table td:not(:first-child), .schedule-table th:not(:first-child) { width: 15.6%; }
          .schedule-table .count-row td { background: #f5f5f5; font-weight: bold; }
          .visiting { font-style: italic; color: #666; }
          .visiting::before { content: "↳ "; }
          .visiting-label {
            font-size: 9px;
            background: #e0e0e0;
            padding: 1px 4px;
            border-radius: 3px;
            margin-left: 4px;
          }
          .notes-section {
            margin-top: 15px;
            padding: 10px;
            background: #f9f9f9;
            border: 1px solid #ddd;
            border-radius: 4px;
            break-inside: avoid;
            page-break-inside: avoid;
          }
          .notes-section .section-title { border-bottom: none; margin-bottom: 6px; }
          .note-item {
            font-size: 10px;
            margin-bottom: 4px;
            padding-left: 12px;
            position: relative;
          }
          .note-item::before { content: "•"; position: absolute; left: 0; }
          .note-label { font-weight: bold; color: #555; }
          .empty-cell { color: #999; }
          .absent-cell { color: #c00; font-style: italic; }
          .age-label { color: #666; font-weight: normal; font-size: 9px; }
          .teacher-summary {
            padding: 20px;
            border: 2px solid #333;
            border-radius: 8px;
          }
          .teacher-table td {
            vertical-align: top;
            font-size: 9px;
            line-height: 1.4;
          }
          .teacher-table td div {
            white-space: nowrap;
            margin-bottom: 2px;
          }
          .teacher-table td div:last-child {
            margin-bottom: 0;
          }
          .utility-assignment {
            color: #666;
            font-style: italic;
          }
          .pto-assignment {
            color: #c00;
            font-weight: bold;
          }
        </style>
      </head>
      <body>
        ${printContent.innerHTML}
      </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
      printWindow.close();
    }, 250);
  };
  
  // Modal styles
  const modalOverlay = {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0, 0, 0, 0.7)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    zIndex: 1000
  };
  
  const modalContent = {
    backgroundColor: darkMode ? '#1a1a2e' : '#fff',
    borderRadius: '12px',
    padding: '24px',
    maxWidth: '550px',
    width: '90%',
    maxHeight: '90vh',
    overflow: 'auto',
    color: darkMode ? '#e0e0e0' : '#333'
  };
  
  const buttonStyle = {
    padding: '10px 20px',
    borderRadius: '6px',
    border: 'none',
    cursor: 'pointer',
    fontWeight: '500',
    fontSize: '14px'
  };
  
  const checkboxStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    padding: '8px 12px',
    backgroundColor: darkMode ? '#2a2a3e' : '#f5f5f5',
    borderRadius: '6px',
    cursor: 'pointer',
    marginBottom: '6px'
  };
  
  if (!isOpen) return null;
  
  const weekDates = getWeekDates(currentMonday);
  const selectedRooms = rooms.filter(r => selectedRoomIds.includes(r.id));
  
  // Get all unique staff across the week for a room
  const getUniqueStaffForRoom = (roomId) => {
    const staffSet = new Map();
    weekDates.forEach(date => {
      const dateStr = formatDate(date);
      const staff = getStaffForRoomDate(roomId, dateStr);
      staff.forEach(s => {
        if (!staffSet.has(s.userId)) {
          staffSet.set(s.userId, s.name);
        }
      });
    });
    return Array.from(staffSet.entries()).map(([id, name]) => ({ id, name }));
  };
  
  // Get all unique children across the week for a room
  const getUniqueChildrenForRoom = (roomId) => {
    const childMap = new Map();
    weekDates.forEach(date => {
      const dateStr = formatDate(date);
      const kids = getChildrenForRoomDate(roomId, dateStr);
      kids.forEach(k => {
        if (!childMap.has(k.child.id)) {
          childMap.set(k.child.id, {
            id: k.child.id,
            name: k.name,
            dateOfBirth: k.child.dateOfBirth,
            hasVisits: k.isVisiting
          });
        } else if (k.isVisiting) {
          const existing = childMap.get(k.child.id);
          existing.hasVisits = true;
        }
      });
    });
    return Array.from(childMap.values());
  };
  
  // Get unique staff for a utility room across the week
  const getUniqueUtilityStaff = (utilRoomId) => {
    const staffSet = new Map();
    const utilSched = utilitySchedules[utilRoomId] || {};
    weekDates.forEach(date => {
      const dateStr = formatDate(date);
      const assignments = utilSched[dateStr] || [];
      assignments.forEach(a => {
        const user = users.find(u => u.id === a.userId);
        if (user && !staffSet.has(a.userId)) {
          staffSet.set(a.userId, user.name);
        }
      });
    });
    return Array.from(staffSet.entries()).map(([id, name]) => ({ id, name }));
  };
  
  // Get all teacher assignments for the week (for teacher summary view)
  const getTeacherWeekSummary = () => {
    const teacherMap = new Map();
    const dates = getWeekDates(currentMonday);
    
    // Initialize all users (teachers and admins) in the map
    users.forEach(user => {
      teacherMap.set(user.id, {
        id: user.id,
        name: user.name,
        role: user.role,
        assignments: {}
      });
    });
    
    // Go through all rooms (including utility) and all dates to add assignments
    const allRooms = [...rooms, ...utilityRooms];
    
    dates.forEach(date => {
      const dateStr = formatDate(date);
      const daySchedule = schedule[dateStr] || {};
      
      allRooms.forEach(room => {
        const roomAssignments = daySchedule[room.id] || [];
        roomAssignments.forEach(assignment => {
          const teacher = teacherMap.get(assignment.userId);
          if (teacher) {
            if (!teacher.assignments[dateStr]) {
              teacher.assignments[dateStr] = [];
            }
            teacher.assignments[dateStr].push({
              room: room.name,
              roomShort: room.shortName || room.name,
              startTime: assignment.startTime,
              endTime: assignment.endTime,
              isUtility: room.isUtility
            });
          }
        });
      });
    });
    
    // Check for approved PTO for each user on each date
    const approvedPTO = dataStore.getPTORequestsByStatus('Approved');
    
    dates.forEach(date => {
      const dateStr = formatDate(date);
      
      approvedPTO.forEach(pto => {
        if (pto.dates.includes(dateStr)) {
          const teacher = teacherMap.get(pto.userId);
          if (teacher) {
            if (!teacher.assignments[dateStr]) {
              teacher.assignments[dateStr] = [];
            }
            // Add PTO indicator
            teacher.assignments[dateStr].push({
              isPTO: true,
              isPartialDay: pto.isPartialDay,
              partialStartTime: pto.partialStartTime,
              partialHours: pto.partialHours
            });
          }
        }
      });
    });
    
    // Convert to array and sort by name
    return Array.from(teacherMap.values()).sort((a, b) => a.name.localeCompare(b.name));
  };
  
  // Render staff rows for a room
  const renderStaffRows = (roomId) => {
    const uniqueStaff = getUniqueStaffForRoom(roomId);
    
    if (uniqueStaff.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="empty-cell">No staff assigned</td>
        </tr>
      );
    }
    
    return uniqueStaff.map(staff => (
      <tr key={staff.id}>
        <td>{staff.name}</td>
        {weekDates.map((date, i) => {
          const dateStr = formatDate(date);
          const dayStaff = getStaffForRoomDate(roomId, dateStr);
          const assignment = dayStaff.find(s => s.userId === staff.id);
          return (
            <td key={i}>
              {assignment ? formatTimeRange(assignment.startTime, assignment.endTime) : '—'}
            </td>
          );
        })}
      </tr>
    ));
  };
  
  // Render student rows for a room
  const renderStudentRows = (roomId) => {
    const uniqueChildren = getUniqueChildrenForRoom(roomId);
    
    if (uniqueChildren.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="empty-cell">No students scheduled</td>
        </tr>
      );
    }
    
    return uniqueChildren.map(child => (
      <tr key={child.id}>
        <td>{child.name} {child.dateOfBirth && <span className="age-label">({formatBirthday(child.dateOfBirth)})</span>}</td>
        {weekDates.map((date, i) => {
          const dateStr = formatDate(date);
          const dayChildren = getChildrenForRoomDate(roomId, dateStr);
          const assignment = dayChildren.find(c => c.child.id === child.id);
          if (!assignment) {
            return <td key={i}>—</td>;
          }
          
          // Check if absent
          if (assignment.isAbsent) {
            return <td key={i} className="absent-cell">Absent</td>;
          }
          
          const timeStr = formatTimeRange(assignment.startTime, assignment.endTime);
          return (
            <td key={i} className={assignment.isVisiting ? 'visiting' : ''}>
              {assignment.isVisiting ? (
                <span>{timeStr} <span className="visiting-label">visiting</span></span>
              ) : (
                timeStr
              )}
            </td>
          );
        })}
      </tr>
    ));
  };
  
  // Render student count row (excludes absent children)
  const renderCountRow = (roomId) => {
    return (
      <tr className="count-row">
        <td><strong>COUNT</strong></td>
        {weekDates.map((date, i) => {
          const dateStr = formatDate(date);
          const dayChildren = getChildrenForRoomDate(roomId, dateStr);
          const presentCount = dayChildren.filter(c => !c.isAbsent).length;
          return <td key={i}><strong>{presentCount}</strong></td>;
        })}
      </tr>
    );
  };
  
  // Render utility room rows (breaks, lunch, etc.)
  const renderUtilityRows = (utilRoomId) => {
    const uniqueStaff = getUniqueUtilityStaff(utilRoomId);
    const utilSched = utilitySchedules[utilRoomId] || {};
    
    if (uniqueStaff.length === 0) {
      return (
        <tr>
          <td colSpan={6} className="empty-cell">No coverage assigned</td>
        </tr>
      );
    }
    
    return uniqueStaff.map(staff => (
      <tr key={staff.id}>
        <td>{staff.name}</td>
        {weekDates.map((date, i) => {
          const dateStr = formatDate(date);
          const assignments = utilSched[dateStr] || [];
          const assignment = assignments.find(a => a.userId === staff.id);
          return (
            <td key={i}>
              {assignment ? formatTimeRange(assignment.startTime, assignment.endTime) : '—'}
            </td>
          );
        })}
      </tr>
    ));
  };
  
  // Render a single room card
  const renderRoomCard = (room, isLast, index) => {
    const roomWeekNotes = getRoomWeekNotes(room.id);
    
    return (
      <div key={room.id} className="room-card" style={{ pageBreakAfter: isLast ? 'auto' : 'always' }}>
        {/* Header */}
        <div className="card-header">
          <div>
            <h1 className="room-name">{room.name}</h1>
            {room.ratio && (
              <p className="room-ratio">Ratio: {room.ratio}:1</p>
            )}
          </div>
          <div className="header-right">
            <p className="week-range">Week of {getWeekDateRange()}</p>
            {weekTheme && (
              <p className="week-theme">Theme: {weekTheme}</p>
            )}
          </div>
        </div>
        
        {/* Announcements Section - Only on first room */}
        {index === 0 && announcements.length > 0 && (
          <div className="announcements-section" style={{
            marginBottom: '12px',
            padding: '8px 12px',
            border: '1px solid #999',
            borderRadius: '4px',
            backgroundColor: '#f9f9f9'
          }}>
            <p style={{ fontWeight: 'bold', margin: '0 0 6px 0', fontSize: '11px' }}>Announcements</p>
            {announcements.map(ann => (
              <p key={ann.id} style={{ margin: '3px 0', fontSize: '10px', paddingLeft: '16px' }}>
                {new Date(ann.date + 'T12:00:00').toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' })}: {ann.message}
              </p>
            ))}
          </div>
        )}
        
        {/* Staff Section */}
        <div className="section">
          <h3 className="section-title">Staff</h3>
          <table className="schedule-table">
            <thead>
              <tr>
                <th></th>
                {weekDates.map((date, i) => (
                  <th key={i}>{getDayAbbrev(i)}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {renderStaffRows(room.id)}
            </tbody>
          </table>
        </div>
        
        {/* Students Section */}
        {includeStudents && (
          <div className="section">
            <h3 className="section-title">Students</h3>
            <table className="schedule-table">
              <thead>
                <tr>
                  <th></th>
                  {weekDates.map((date, i) => (
                    <th key={i}>{getDayAbbrev(i)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderStudentRows(room.id)}
                {renderCountRow(room.id)}
              </tbody>
            </table>
          </div>
        )}
        
        {/* Utility Room Sections (Breaks, Lunch, etc.) */}
        {utilityRooms.map(utilRoom => (
          <div className="section" key={utilRoom.id}>
            <h3 className="section-title">{utilRoom.name}</h3>
            <table className="schedule-table">
              <thead>
                <tr>
                  <th></th>
                  {weekDates.map((date, i) => (
                    <th key={i}>{getDayAbbrev(i)}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {renderUtilityRows(utilRoom.id)}
              </tbody>
            </table>
          </div>
        ))}
        
        {/* Notes Section - only room notes, theme is in header */}
        {roomWeekNotes.length > 0 && (
          <div className="notes-section">
            <h3 className="section-title">Notes</h3>
            {roomWeekNotes.map((note, i) => (
              <p key={i} className="note-item">
                <span className="note-label">{note.day}:</span> {note.text}
              </p>
            ))}
          </div>
        )}
      </div>
    );
  };
  
  // Render teacher summary page (single page with all teachers)
  const renderTeacherSummary = () => {
    const teachers = getTeacherWeekSummary();
    const dates = getWeekDates(currentMonday);
    
    return (
      <div className="teacher-summary">
        {/* Header */}
        <div className="card-header">
          <div>
            <h1 className="room-name">Teacher Schedule</h1>
          </div>
          <div className="header-right">
            <p className="week-range">Week of {getWeekDateRange()}</p>
            {weekTheme && (
              <p className="week-theme">Theme: {weekTheme}</p>
            )}
          </div>
        </div>
        
        {/* Teacher Schedule Table */}
        <table className="schedule-table teacher-table">
          <thead>
            <tr>
              <th>Teacher</th>
              {dates.map((date, i) => (
                <th key={i}>{getDayAbbrev(i)}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {teachers.map(teacher => (
              <tr key={teacher.id}>
                <td>{teacher.name}</td>
                {dates.map((date, i) => {
                  const dateStr = formatDate(date);
                  const dayAssignments = teacher.assignments[dateStr] || [];
                  
                  if (dayAssignments.length === 0) {
                    return <td key={i} className="empty-cell">—</td>;
                  }
                  
                  // Separate PTO from room assignments
                  const ptoEntry = dayAssignments.find(a => a.isPTO);
                  const roomAssignments = dayAssignments.filter(a => !a.isPTO);
                  
                  // Sort room assignments: regular rooms first, then utility
                  const sorted = [...roomAssignments].sort((a, b) => {
                    if (a.isUtility && !b.isUtility) return 1;
                    if (!a.isUtility && b.isUtility) return -1;
                    return a.startTime.localeCompare(b.startTime);
                  });
                  
                  return (
                    <td key={i}>
                      {ptoEntry && (
                        <div className="pto-assignment">
                          {ptoEntry.isPartialDay ? `PTO (${ptoEntry.partialHours}h)` : 'PTO'}
                        </div>
                      )}
                      {sorted.map((a, idx) => (
                        <div key={idx} className={a.isUtility ? 'utility-assignment' : ''}>
                          {a.roomShort}: {formatTimeRange(a.startTime, a.endTime)}
                        </div>
                      ))}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    );
  };
  
  return (
    <div style={modalOverlay} onClick={onClose}>
      <div style={modalContent} onClick={(e) => e.stopPropagation()}>
        <h2 style={{ marginBottom: '16px', fontSize: '20px' }}>Print Room Cards</h2>
        
        {/* Print Mode Selection */}
        <div style={{ marginBottom: '16px' }}>
          <span style={{ fontWeight: '500', display: 'block', marginBottom: '10px' }}>Print Mode:</span>
          <div style={{ display: 'flex', gap: '16px' }}>
            <label style={{ ...checkboxStyle, flex: '1' }}>
              <input
                type="radio"
                name="printMode"
                checked={printMode === 'rooms'}
                onChange={() => setPrintMode('rooms')}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <div>
                <span style={{ fontWeight: '500' }}>Room Cards</span>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: darkMode ? '#888' : '#666' }}>
                  One page per room with staff, students, coverage
                </p>
              </div>
            </label>
            <label style={{ ...checkboxStyle, flex: '1' }}>
              <input
                type="radio"
                name="printMode"
                checked={printMode === 'teacherSummary'}
                onChange={() => setPrintMode('teacherSummary')}
                style={{ width: '18px', height: '18px', cursor: 'pointer' }}
              />
              <div>
                <span style={{ fontWeight: '500' }}>Teacher Summary</span>
                <p style={{ margin: '4px 0 0 0', fontSize: '12px', color: darkMode ? '#888' : '#666' }}>
                  Single page with all teachers and their rooms
                </p>
              </div>
            </label>
          </div>
        </div>
        
        {/* Room Cards specific options */}
        {printMode === 'rooms' && (
          <>
            <p style={{ marginBottom: '16px', color: darkMode ? '#aaa' : '#666' }}>
              Select which rooms to print. Each room prints on its own page with staff, students, 
              and coverage schedules.
            </p>
        
            {/* Room Selection */}
            <div style={{ marginBottom: '16px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
            <span style={{ fontWeight: '500' }}>Rooms to Print:</span>
            <div style={{ display: 'flex', gap: '8px' }}>
              <button
                onClick={selectAllRooms}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  border: `1px solid ${darkMode ? '#555' : '#ccc'}`,
                  backgroundColor: 'transparent',
                  color: darkMode ? '#aaa' : '#666',
                  cursor: 'pointer'
                }}
              >
                Select All
              </button>
              <button
                onClick={deselectAllRooms}
                style={{
                  padding: '4px 10px',
                  fontSize: '12px',
                  borderRadius: '4px',
                  border: `1px solid ${darkMode ? '#555' : '#ccc'}`,
                  backgroundColor: 'transparent',
                  color: darkMode ? '#aaa' : '#666',
                  cursor: 'pointer'
                }}
              >
                Deselect All
              </button>
            </div>
          </div>
          
          <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
            {rooms.map(room => (
              <label key={room.id} style={checkboxStyle}>
                <input
                  type="checkbox"
                  checked={selectedRoomIds.includes(room.id)}
                  onChange={() => toggleRoom(room.id)}
                  style={{ width: '18px', height: '18px', cursor: 'pointer' }}
                />
                <span>{room.name}</span>
                {room.ratio && (
                  <span style={{ color: darkMode ? '#888' : '#999', fontSize: '12px' }}>
                    ({room.ratio}:1)
                  </span>
                )}
              </label>
            ))}
          </div>
        </div>
        
        {/* Print Options */}
        <div style={{ marginBottom: '16px' }}>
          <span style={{ fontWeight: '500', display: 'block', marginBottom: '10px' }}>Print Options:</span>
          <label style={checkboxStyle}>
            <input
              type="checkbox"
              checked={includeStudents}
              onChange={() => setIncludeStudents(!includeStudents)}
              style={{ width: '18px', height: '18px', cursor: 'pointer' }}
            />
            <span>Include Students Section</span>
          </label>
        </div>
          </>
        )}
        
        {/* Summary */}
        <div style={{ 
          padding: '12px', 
          backgroundColor: darkMode ? '#2a2a3e' : '#f5f5f5',
          borderRadius: '8px',
          marginBottom: '20px'
        }}>
          <p style={{ fontWeight: '500', marginBottom: '8px' }}>Week: {getWeekDateRange()}</p>
          {weekTheme && <p style={{ color: darkMode ? '#aaa' : '#666', marginBottom: '8px' }}>Theme: {weekTheme}</p>}
          {printMode === 'rooms' && utilityRooms.length > 0 && (
            <p style={{ fontSize: '13px', color: darkMode ? '#888' : '#888' }}>
              📋 {utilityRooms.map(r => r.name).join(', ')} coverage will appear on each card.
            </p>
          )}
          {printMode === 'teacherSummary' && (
            <p style={{ fontSize: '13px', color: darkMode ? '#888' : '#888' }}>
              👥 Shows all teachers with their room assignments for the week.
            </p>
          )}
        </div>
        
        {/* Buttons */}
        <div style={{ display: 'flex', gap: '12px', justifyContent: 'flex-end' }}>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: 'transparent',
              border: `1px solid ${darkMode ? '#555' : '#ccc'}`,
              color: darkMode ? '#e0e0e0' : '#333'
            }}
            onClick={onClose}
          >
            Cancel
          </button>
          <button
            style={{
              ...buttonStyle,
              backgroundColor: (printMode === 'teacherSummary' || selectedRooms.length > 0) ? '#77b8db' : '#999',
              color: darkMode ? '#1a1a2e' : '#fff',
              cursor: (printMode === 'teacherSummary' || selectedRooms.length > 0) ? 'pointer' : 'not-allowed'
            }}
            onClick={handlePrint}
            disabled={printMode === 'rooms' && selectedRooms.length === 0}
          >
            🖨️ {printMode === 'teacherSummary' ? 'Print Teacher Summary' : `Print ${selectedRooms.length} Room${selectedRooms.length !== 1 ? 's' : ''}`}
          </button>
        </div>
        
        {/* Hidden print content */}
        <div ref={printRef} style={{ display: 'none' }}>
          {printMode === 'rooms' 
            ? selectedRooms.map((room, index) => renderRoomCard(room, index === selectedRooms.length - 1, index))
            : renderTeacherSummary()
          }
        </div>
      </div>
    </div>
  );
};

export default RoomCardPrint;
